import React from 'react';
import { Navigate } from 'react-router-dom';
import { ChevronDown, Info, Lock } from 'lucide-react';
import { useUser } from '@/providers/UserProvider';
import {
  useNotificationPreferences,
  type NotificationLevel,
  type NotificationTypeRow,
  type PreferenceUpdate,
} from '@/hooks/notifications/useNotificationPreferences';
import type { LevelState } from '@/hooks/notifications/notificationPreferencesApi';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// "My submissions" leads: it is the column that follows the user everywhere,
// and it wins over the other two for anything they filed themselves.
const LEVELS: NotificationLevel[] = ['submission', 'site', 'study'];

const LEVEL_COPY: Record<
  NotificationLevel,
  { label: string; subtitle: string; tooltip: string }
> = {
  submission: {
    label: 'My submissions',
    subtitle: 'Ones you filed',
    tooltip:
      'Submissions you filed, or were added to directly. These settings apply in every study and site, and take priority over the Site and Study columns.',
  },
  site: {
    label: 'Site level',
    subtitle: 'Sites you’re on',
    tooltip:
      'Submissions someone else filed under a site where you have a role.',
  },
  study: {
    label: 'Study level',
    subtitle: 'Studies you’re on',
    tooltip:
      'Submissions someone else filed under a study where you have a role — including its sites’ submissions, unless you are also on that site.',
  },
};

const STATUS_CHANGE_GROUP = 'status_change';

// Frames the My submissions column top to bottom. The header cell takes only
// the border, so the header row stays one uniform shade; body cells also get
// a light tint.
const HIGHLIGHT_BORDER = 'border-x border-gray-200';
const HIGHLIGHT = cn('bg-gray-50', HIGHLIGHT_BORDER);

// Tri-state: true/false/'indeterminate' — a plain boolean can't represent a
// select-all checkbox where some but not all of its cells are enabled.
type CheckedState = boolean | 'indeterminate';

interface Cell {
  row: NotificationTypeRow;
  level: NotificationLevel;
  state: LevelState;
}

// Only cells the level actually applies to count. Counting a non-applicable
// cell as "off" would peg a select-all box to off (or indeterminate) no matter
// what the user does to the cells they can reach. `row.levels[level]` is read
// defensively: an absent key is not applicable, rather than a crash.
const applicableCells = (
  rows: NotificationTypeRow[],
  levels: NotificationLevel[]
): Cell[] =>
  rows.flatMap(row =>
    levels.flatMap(level => {
      const state = row.levels?.[level];
      return state?.applicable ? [{ row, level, state }] : [];
    })
  );

const selectAllState = (
  cells: Cell[]
): { checked: CheckedState; locked: boolean; applicable: boolean } => {
  if (cells.length === 0) {
    return { checked: false, locked: true, applicable: false };
  }

  const allOn = cells.every(cell => cell.state.enabled);
  const allOff = cells.every(cell => !cell.state.enabled);
  return {
    checked: allOn ? true : allOff ? false : 'indeterminate',
    // Locked only when there is nothing left for the box to change. One
    // admin-locked cell must not freeze the rest: updatesFor skips locked
    // cells, and unchecking the box is how the user gets back to editing the
    // cells it covers.
    locked: cells.every(cell => cell.state.locked),
    applicable: true,
  };
};

// Skips what the server would reject anyway: a locked cell is admin-owned,
// so firing an update for it only produces an error the user never asked for.
const updatesFor = (cells: Cell[], enabled: boolean): PreferenceUpdate[] =>
  cells
    .filter(cell => !cell.state.locked)
    .map(cell => ({
      notificationType: cell.row.notification_type,
      level: cell.level,
      enabled,
    }));

interface CheckboxCellProps {
  state: { applicable: boolean; enabled: boolean; locked: boolean } | undefined;
  checked?: CheckedState;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
  ariaLabel: string;
}

const CheckboxCell: React.FC<CheckboxCellProps> = ({
  state,
  checked,
  disabled,
  onChange,
  ariaLabel,
}) => {
  if (state && !state.applicable) {
    return (
      <div className="flex justify-center">
        <span className="text-gray-300" aria-hidden="true">
          —
        </span>
      </div>
    );
  }

  const isChecked = checked ?? state?.enabled ?? false;
  const isLocked = disabled ?? state?.locked ?? false;

  const box = (
    <Checkbox
      checked={isChecked}
      disabled={isLocked || !onChange}
      onCheckedChange={value => onChange?.(value === true)}
      aria-label={ariaLabel}
    />
  );

  return (
    <div className="flex justify-center">
      {isLocked && state?.locked ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span>{box}</span>
          </TooltipTrigger>
          <TooltipContent>
            An administrator has set this notification and it can&apos;t be
            changed here.
          </TooltipContent>
        </Tooltip>
      ) : (
        box
      )}
    </div>
  );
};

// The (i) icon used by both the column headers and the row labels, so the two
// explanations look and behave the same.
const InfoTooltip: React.FC<{ label: string; content: string }> = ({
  label,
  content,
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        className="text-gray-400 hover:text-gray-600"
        aria-label={`About ${label}`}
      >
        <Info className="h-3.5 w-3.5" />
      </button>
    </TooltipTrigger>
    <TooltipContent className="max-w-xs font-normal">{content}</TooltipContent>
  </Tooltip>
);

const levelCellClass = (level: NotificationLevel) =>
  cn('px-4', level === 'submission' && HIGHLIGHT);

const NotificationPreferences: React.FC = () => {
  const { userData, loading: userLoading, error: userError } = useUser();
  const {
    preferences,
    isLoading,
    error,
    updateError,
    updatePreferences,
    isUpdating,
  } = useNotificationPreferences();
  const [statusChangesExpanded, setStatusChangesExpanded] =
    React.useState(true);

  if (userLoading) {
    return <p className="text-sm text-gray-500 p-6">Loading…</p>;
  }

  // A redirect is the answer to "the feature is off", not to "we never found
  // out". A failed /users/me leaves userData null, and bouncing on that is
  // indistinguishable to the user from the feature being dormant.
  if (userError || !userData) {
    return (
      <p className="text-sm text-red-600 p-6">
        {userError ?? 'Could not load your account. Please try again.'}
      </p>
    );
  }

  // Fully dormant pre-launch: while the flag is off, this page redirects
  // away rather than rendering a disabled state — there's nothing to see.
  if (!userData.notification_personalization_enabled) {
    return <Navigate to="/dashboard" replace />;
  }

  const statusChangeRows = preferences.filter(
    row => row.group === STATUS_CHANGE_GROUP
  );
  const otherRows = preferences.filter(
    row => row.group !== STATUS_CHANGE_GROUP && row.category !== 'other'
  );
  return (
    <TooltipProvider>
      <div className="max-w-6xl mx-auto py-8 px-6">
        <div className="flex items-start justify-between mb-1">
          <div>
            <p className="text-sm text-gray-500">Settings</p>
            <h1 className="text-2xl font-semibold text-gray-900">
              Notification preferences
            </h1>
          </div>
          <span
            className={cn(
              'text-sm rounded-full px-3 py-1',
              isUpdating
                ? 'text-gray-600 bg-gray-100'
                : 'text-green-700 bg-green-50'
            )}
          >
            {isUpdating ? 'Saving…' : '✓ All changes saved'}
          </span>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Choose which emails OneVerse sends you. Changes save as you make them.
        </p>

        <div className="mb-4 bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-900 space-y-2">
          <p>
            Each email follows exactly one column.{' '}
            <strong>My submissions</strong> covers everything you filed
            yourself, wherever it lives. For submissions filed by someone else,{' '}
            <strong>Site level</strong> applies if you&apos;re on its site,
            otherwise <strong>Study level</strong> if you&apos;re on its study.
          </p>
          <p>
            Your access decides what can arrive at all: you are only emailed
            about studies, sites and submissions you can already open in
            OneVerse. Turning something on here never gives you access you do
            not already have.
          </p>
        </div>

        <div className="flex items-center justify-end gap-5 text-xs text-gray-500 mb-3">
          {/* Legend swatches are display-only: "On" shows the same vivid,
              interactive look a toggleable checked box has, while "Locked"
              is checked but disabled (greyed) — same visual rule the table
              cells below follow, so it's a real key rather than decoration. */}
          <span className="flex items-center gap-1.5 pointer-events-none">
            <Checkbox checked={false} tabIndex={-1} aria-hidden="true" /> Off
          </span>
          <span className="flex items-center gap-1.5 pointer-events-none">
            <Checkbox checked tabIndex={-1} aria-hidden="true" /> On
          </span>
          <span className="flex items-center gap-1.5 pointer-events-none">
            <Checkbox checked disabled tabIndex={-1} aria-hidden="true" />
            Locked
          </span>
        </div>

        {isLoading && (
          <p className="text-sm text-gray-500">Loading preferences…</p>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {/* A rejected toggle re-reads the server's value, so without this the
            checkbox just snaps back with no explanation. */}
        {updateError && (
          <p className="text-sm text-red-600" role="alert">
            {updateError}
          </p>
        )}

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-gray-100 text-xs text-gray-600 align-top">
                <th className="text-left font-medium px-6 py-3">
                  Notification
                </th>
                {LEVELS.map(level => {
                  const copy = LEVEL_COPY[level];
                  return (
                    <th
                      key={level}
                      scope="col"
                      className={cn(
                        'w-44 font-medium py-3',
                        'px-4',
                        level === 'submission' && HIGHLIGHT_BORDER
                      )}
                    >
                      <div className="flex flex-col items-center gap-1.5">
                        <span className="flex items-center gap-1">
                          {copy.label}
                          <InfoTooltip
                            label={copy.label}
                            content={copy.tooltip}
                          />
                        </span>
                        <span className="font-normal text-gray-500">
                          {copy.subtitle}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {statusChangeRows.length > 0 && (
                <tr className="border-t border-gray-200">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        className="flex items-center gap-2 text-left"
                        onClick={() => setStatusChangesExpanded(prev => !prev)}
                        aria-expanded={statusChangesExpanded}
                      >
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 text-gray-400 transition-transform',
                            !statusChangesExpanded && '-rotate-90'
                          )}
                        />
                        <span>
                          <span className="font-medium text-gray-900 block">
                            All status changes
                          </span>
                          <span className="text-xs text-gray-500">
                            Submitted, pre-review, IRB review, post-review, and
                            complete
                          </span>
                        </span>
                      </button>
                    </div>
                  </td>
                  {LEVELS.map(level => {
                    const group = selectAllState(
                      applicableCells(statusChangeRows, [level])
                    );
                    return (
                      <td key={level} className={levelCellClass(level)}>
                        <CheckboxCell
                          state={{
                            applicable: group.applicable,
                            enabled: false,
                            locked: group.locked,
                          }}
                          checked={group.checked}
                          ariaLabel={`All status changes, ${LEVEL_COPY[level].label}`}
                          onChange={next =>
                            updatePreferences(
                              updatesFor(
                                applicableCells(statusChangeRows, [level]),
                                next
                              )
                            )
                          }
                        />
                      </td>
                    );
                  })}
                </tr>
              )}

              {statusChangesExpanded &&
                statusChangeRows.map((row, index) => (
                  <React.Fragment key={row.notification_type}>
                    <tr className="border-t border-gray-100">
                      <td className="py-3 pl-14 pr-6 text-gray-700">
                        <span className="flex items-center gap-1">
                          {row.label}
                          {row.description && (
                            <InfoTooltip
                              label={row.label}
                              content={row.description}
                            />
                          )}
                        </span>
                      </td>
                      {LEVELS.map(level => {
                        // Disabled (grayed, but reflecting the implied "on")
                        // exactly while "All status changes" is fully on for
                        // this column — uncheck it first to customize
                        // individually.
                        const disabledByGroup =
                          selectAllState(
                            applicableCells(statusChangeRows, [level])
                          ).checked === true;
                        return (
                          <td key={level} className={levelCellClass(level)}>
                            <CheckboxCell
                              state={row.levels[level]}
                              disabled={
                                disabledByGroup || row.levels[level].locked
                              }
                              ariaLabel={`${row.label}, ${LEVEL_COPY[level].label}`}
                              onChange={
                                disabledByGroup
                                  ? undefined
                                  : next =>
                                      updatePreferences([
                                        {
                                          notificationType:
                                            row.notification_type,
                                          level,
                                          enabled: next,
                                        },
                                      ])
                              }
                            />
                          </td>
                        );
                      })}
                    </tr>
                    {index === statusChangeRows.length - 1 && (
                      <tr>
                        <td className="pl-14 pr-6 pb-3 text-xs text-gray-400">
                          Pre-review and post-review arrive only with all status
                          changes.
                        </td>
                        {LEVELS.map(level => (
                          <td key={level} className={levelCellClass(level)} />
                        ))}
                      </tr>
                    )}
                  </React.Fragment>
                ))}

              {otherRows.map(row => (
                <tr
                  key={row.notification_type}
                  className="border-t border-gray-200"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {row.mandatory && (
                        <Lock className="h-4 w-4 text-gray-400 shrink-0" />
                      )}
                      <span>
                        <span
                          className={cn(
                            'font-medium flex items-center gap-1',
                            row.mandatory ? 'text-gray-500' : 'text-gray-900'
                          )}
                        >
                          {row.label}
                          {row.description && (
                            <InfoTooltip
                              label={row.label}
                              content={row.description}
                            />
                          )}
                        </span>
                        {row.mandatory && (
                          <span className="text-xs text-amber-600">
                            Required
                          </span>
                        )}
                      </span>
                    </div>
                  </td>
                  {LEVELS.map(level => (
                    <td key={level} className={levelCellClass(level)}>
                      <CheckboxCell
                        state={row.levels[level]}
                        ariaLabel={`${row.label}, ${LEVEL_COPY[level].label}`}
                        onChange={
                          row.mandatory
                            ? undefined
                            : next =>
                                updatePreferences([
                                  {
                                    notificationType: row.notification_type,
                                    level,
                                    enabled: next,
                                  },
                                ])
                        }
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default NotificationPreferences;
